import { useCallback, useMemo, useState } from 'react'
import { PasswordRuleValidators, type PasswordRules } from '../dtos/Account/PasswordRules'

export interface UsePasswordGeneratorProps {
    rules: PasswordRules;
    length?: number;
}

export interface UsePasswordGeneratorReturn {
    password: string;
    generatePassword: () => void;
    isValid: boolean;
    validationErrors: string[];
}

const LOWERCASE_CHARS = 'abcdefghijklmnopqrstuvwxyz'
const UPPERCASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const DIGIT_CHARS = '0123456789'
const SPECIAL_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?'

export const usePasswordGenerator = ({ rules, length = 16 }: UsePasswordGeneratorProps): UsePasswordGeneratorReturn => {
    const [password, setPassword] = useState<string>('')

    const generatePassword = useCallback(() => {
        let charset = ''
        let guaranteedChars = ''
        
        // Build charset based on rules
        if (rules.requireLowercase || !rules.requireUppercase) {
            charset += LOWERCASE_CHARS
            if (rules.requireLowercase) {
                guaranteedChars += LOWERCASE_CHARS.charAt(Math.floor(Math.random() * LOWERCASE_CHARS.length))
            }
        }
        
        if (rules.requireUppercase) {
            charset += UPPERCASE_CHARS
            guaranteedChars += UPPERCASE_CHARS.charAt(Math.floor(Math.random() * UPPERCASE_CHARS.length))
        }
        
        if (rules.requireDigit) {
            charset += DIGIT_CHARS
            guaranteedChars += DIGIT_CHARS.charAt(Math.floor(Math.random() * DIGIT_CHARS.length))
        }
        
        if (rules.requireNonLetterOrDigit) {
            charset += SPECIAL_CHARS
            guaranteedChars += SPECIAL_CHARS.charAt(Math.floor(Math.random() * SPECIAL_CHARS.length))
        }
        
        // If no specific requirements, use all characters
        if (charset === '') {
            charset = LOWERCASE_CHARS + UPPERCASE_CHARS + DIGIT_CHARS
        }
        
        // Calculate the actual password length
        const passwordLength = Math.max(rules.minLenght || 8, length)
        
        // Generate remaining characters
        let remainingChars = ''
        for (let i = guaranteedChars.length; i < passwordLength; i++) {
            remainingChars += charset.charAt(Math.floor(Math.random() * charset.length))
        }
        
        // Combine guaranteed and remaining characters
        const allChars = guaranteedChars + remainingChars
        
        // Shuffle the characters to avoid predictable patterns
        const shuffled = allChars.split('').sort(() => Math.random() - 0.5).join('')
        
        setPassword(shuffled)
    }, [rules, length])

    const { isValid, validationErrors } = useMemo(() => {
        const errors: string[] = []
        let valid = true

        if (!password) {
            return { isValid: false, validationErrors: [] }
        }

        PasswordRuleValidators.forEach(([key, validatorFactory]) => {
            const ruleValue = rules[key]
            if (ruleValue !== undefined && ruleValue !== false && ruleValue !== 0) {
                const validator = validatorFactory(ruleValue)
                if (!validator(password)) {
                    valid = false
                    errors.push(`Rule '${key}' failed`)
                }
            }
        })

        return { isValid: valid, validationErrors: errors }
    }, [password, rules])

    return {
        password,
        generatePassword,
        isValid,
        validationErrors
    }
}